import { useEffect, useState } from 'react';
import { Modal } from '@jywer/ui';
import { fetchAdminSubscription, patchAdminSubscription, type PatchSubInput } from './api';
import {
  SUBSCRIPTION_TYPE_LABEL,
  TRIGGER_EVENT_LABEL,
  type AdminSubscriptionDetail,
  type BillingPeriod,
  type TriggerEvent,
} from './types';

const BASIC_ABO_VALUES = [
  'hosting_af_domaene',
  'webflow_hosting',
  'fri_telefonsupport',
  'cookiebanner',
  'weglot',
  'vimeo_pro',
  'jetboost',
  'webflow_localization',
] as const;

const BASIC_ABO_LABELS: Record<string, string> = {
  hosting_af_domaene: 'Hosting af domæne',
  webflow_hosting: 'Webflow hosting',
  fri_telefonsupport: 'Fri telefonsupport',
  cookiebanner: 'Cookiebanner',
  weglot: 'Weglot',
  vimeo_pro: 'Vimeo Pro',
  jetboost: 'Jetboost',
  webflow_localization: 'Webflow Localization',
};

const TRIGGER_VALUES: TriggerEvent[] = [
  'ved_indgaaelse',
  'ved_opstart',
  'ved_levering',
  'design_godkendt',
  'optagelsesdagen',
  'custom',
];

const PERIOD_VALUES: BillingPeriod[] = ['monthly', 'quarterly', 'halvaarlig', 'yearly'];

const INPUT_CLS =
  'rounded border border-line-2 bg-paper-0 px-2 py-1 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

interface Props {
  id: string;
  onClose: () => void;
  onSaved: () => void;
}

export function EditSubscriptionModal({ id, onClose, onSaved }: Props) {
  const [data, setData] = useState<AdminSubscriptionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [monthlyPrice, setMonthlyPrice] = useState('');
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const [contractLengthMonths, setContractLengthMonths] = useState('');
  const [monthlyHourMinimum, setMonthlyHourMinimum] = useState('');
  const [triggerEvent, setTriggerEvent] = useState<TriggerEvent | ''>('');
  const [setupTriggerTaskId, setSetupTriggerTaskId] = useState('');
  const [contents, setContents] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    (async () => {
      try {
        const d = await fetchAdminSubscription(id);
        if (cancelled) return;
        setData(d);
        const s = d.subscription;
        setMonthlyPrice(s.monthly_price);
        setBillingPeriod(s.billing_period);
        setContractLengthMonths(s.contract_length_months?.toString() ?? '');
        setMonthlyHourMinimum(s.monthly_hour_minimum ?? '');
        setTriggerEvent(s.trigger_event ?? '');
        setSetupTriggerTaskId(s.setup_trigger_task_id ?? '');
        setContents(Array.isArray(s.contents) ? (s.contents as string[]) : []);
      } catch (e) {
        if (cancelled) return;
        setErr(e instanceof Error ? e.message : 'Ukendt fejl');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function save() {
    if (!data) return;
    const s = data.subscription;
    const triggerLocked = data.sub_rate?.firedAt != null;
    setSaving(true);
    setErr(null);
    try {
      const patch: PatchSubInput = {};
      if (monthlyPrice !== s.monthly_price) patch.monthly_price = monthlyPrice;
      if (billingPeriod !== s.billing_period) patch.billing_period = billingPeriod;
      const ctrLen = contractLengthMonths.trim() === '' ? null : Number(contractLengthMonths);
      if (ctrLen !== s.contract_length_months) patch.contract_length_months = ctrLen;
      if (s.subscription_type === 'maas') {
        const min = monthlyHourMinimum.trim() === '' ? null : monthlyHourMinimum;
        if (min !== s.monthly_hour_minimum) patch.monthly_hour_minimum = min;
      }
      if (s.subscription_type === 'basic_hosting_support') {
        const before = JSON.stringify(Array.isArray(s.contents) ? s.contents : []);
        if (JSON.stringify(contents) !== before) patch.basic_abo_contents = contents;
      }
      if (!triggerLocked) {
        if (triggerEvent && triggerEvent !== s.trigger_event) {
          patch.trigger_event = triggerEvent;
        }
        const taskId = setupTriggerTaskId.trim() === '' ? null : setupTriggerTaskId;
        if (taskId !== s.setup_trigger_task_id) patch.setup_trigger_task_id = taskId;
      }
      if (Object.keys(patch).length === 0) {
        onClose();
        return;
      }
      await patchAdminSubscription(id, patch);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Ukendt fejl');
    } finally {
      setSaving(false);
    }
  }

  if (!data && !err && loading) {
    return (
      <Modal open onClose={onClose} title="Rediger abonnement" size="md">
        <p className="text-sm text-ink-2">Henter…</p>
      </Modal>
    );
  }

  if (err) {
    return (
      <Modal open onClose={onClose} title="Rediger abonnement" size="md">
        <p className="text-sm text-status-err">{err}</p>
      </Modal>
    );
  }

  if (!data) return null;
  const s = data.subscription;
  const triggerLocked = data.sub_rate?.firedAt != null;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Rediger ${SUBSCRIPTION_TYPE_LABEL[s.subscription_type]} — ${s.client_name ?? '—'}`}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded border border-line-2 px-3 py-1 text-sm"
          >
            Annullér
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="rounded bg-primary px-3 py-1 text-sm text-white disabled:opacity-50"
          >
            {saving ? 'Gemmer…' : 'Gem'}
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Månedlig pris (DKK)">
            <input
              type="text"
              inputMode="decimal"
              value={monthlyPrice}
              onChange={(e) => setMonthlyPrice(e.target.value)}
              className={INPUT_CLS}
            />
          </Field>
          <Field label="Faktureringsperiode">
            <select
              value={billingPeriod}
              onChange={(e) => setBillingPeriod(e.target.value as BillingPeriod)}
              className={INPUT_CLS}
            >
              {PERIOD_VALUES.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Kontraktlængde (måneder)">
            <input
              type="number"
              min={1}
              max={120}
              value={contractLengthMonths}
              onChange={(e) => setContractLengthMonths(e.target.value)}
              className={INPUT_CLS}
            />
          </Field>
          {s.subscription_type === 'maas' ? (
            <Field label="Månedligt timeminimum">
              <input
                type="text"
                inputMode="decimal"
                value={monthlyHourMinimum}
                onChange={(e) => setMonthlyHourMinimum(e.target.value)}
                className={INPUT_CLS}
                placeholder="f.eks. 10"
              />
            </Field>
          ) : null}
        </div>
        {s.subscription_type === 'basic_hosting_support' ? (
          <Field label="Indhold">
            <div className="flex flex-wrap gap-2">
              {BASIC_ABO_VALUES.map((v) => {
                const checked = contents.includes(v);
                return (
                  <label
                    key={v}
                    className={`flex items-center gap-1 rounded border px-2 py-1 text-xs ${
                      checked ? 'border-primary bg-primary-tint' : 'border-line-2'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) =>
                        setContents((c) =>
                          e.target.checked ? [...c, v] : c.filter((x) => x !== v),
                        )
                      }
                    />
                    {BASIC_ABO_LABELS[v]}
                  </label>
                );
              })}
            </div>
          </Field>
        ) : null}
        <fieldset disabled={triggerLocked} className="flex flex-col gap-3">
          {triggerLocked ? (
            <p className="text-xs text-ink-2">Trigger er fyret — kan ikke ændres.</p>
          ) : null}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Trigger-event">
              <select
                value={triggerEvent}
                onChange={(e) => setTriggerEvent(e.target.value as TriggerEvent | '')}
                className={INPUT_CLS}
              >
                <option value="">— ikke sat —</option>
                {TRIGGER_VALUES.map((v) => (
                  <option key={v} value={v}>
                    {TRIGGER_EVENT_LABEL[v]}
                  </option>
                ))}
              </select>
            </Field>
            {/* Read-only legacy idempotency key. Display-only for historic
                rows — no new entry. The DB column + PATCH path are retained
                (CLICKUP-2/7); the value just can no longer be edited here. */}
            <Field label="Setup-trigger task ID (historisk)">
              <input
                type="text"
                value={setupTriggerTaskId || '—'}
                readOnly
                disabled
                className={`${INPUT_CLS} text-ink-2`}
              />
            </Field>
          </div>
        </fieldset>
        <p className="text-xs text-ink-2">
          Total faktureret til dato: <span className="font-medium">{data.total_invoiced} DKK</span>
        </p>
        {err ? <p className="text-sm text-status-err">{err}</p> : null}
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm text-ink-1">
      <span className="text-xs font-medium uppercase tracking-wide text-ink-2">{label}</span>
      {children}
    </label>
  );
}
